import { useState } from "react";
import { Box, Button, Modal, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PaycloudBranch } from "../../types";
import { useToast } from "../../context/toast-context";
import { CloverReassignBranchModal } from "./CloverReassignBranchModal";
import { CloverMerchantBadge } from "../branches/clover-merchant-badge";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "700px",
  maxWidth: 1200,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "#fff",
  border: "1px solid #898989",
  boxShadow: 24,
  borderRadius: 4,
};

interface CloverConnectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  branches: PaycloudBranch[];
  onBranchReassigned: () => void;
}

export function CloverConnectionModal({ isOpen, onClose, branches, onBranchReassigned }: CloverConnectionModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [branchToReassign, setBranchToReassign] = useState<PaycloudBranch | null>(null);

  const handleConnect = (branch: PaycloudBranch) => {
    if (!branch.cloverExternalId) {
      showToast("No se encontró el Comercio en Clover para esta sucursal", "error");
      return;
    }

    const CLOVER_URL_API = process.env.REACT_APP_CLOVER_URL_API ?? "";
    const CLOVER_CLIENT_ID = process.env.REACT_APP_CLOVER_CLIENT_ID ?? "";
    const REACT_APP_BACKOFFICE_URL = process.env.REACT_APP_BACKOFFICE_URL ?? "";
    
    const authUrl = `${CLOVER_URL_API}/oauth/v2/authorize?client_id=${CLOVER_CLIENT_ID}&response_type=code&redirect_uri=${REACT_APP_BACKOFFICE_URL}/clover-auth&merchant_id=${branch.cloverExternalId}`;
    window.location.href = authUrl;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Modal
        open={isOpen}
        onClose={onClose}
        aria-labelledby="branch-connection-modal"
        aria-describedby="Modal para conectar con sucursales"
      >
        <Box sx={style}>
          <div className="bg-gray-light text-center text-xl font-normal text-black py-8 px-2">
            <h1>Autorización Clover</h1>
          </div>
          <div className="p-6">
            <p className="text-gray-600 text-[15px] leading-7 mb-6">
              Para completar la configuración, es necesario autorizar la conexión con Clover.
              <br />
              Hacé clic en el botón <strong>Autorizar</strong> para continuar.
              <br />
              Si no podés hacerlo ahora, podés realizarlo más tarde desde el menú <strong>Sucursales</strong>.
            </p>
            <div className="space-y-4">
              {branches.map((branch) => (
                <div
                  key={branch.branchId}
                  className="flex justify-between items-center p-4 border border-gray-200 rounded-lg"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Typography variant="h6">{branch.name}</Typography>
                        <CloverMerchantBadge
                          branchId={branch.branchId}
                          merchantId={branch.cloverExternalId}
                          branchName={branch.name}
                          variant="dark"
                          size={18}
                          onUnlinked={() => onBranchReassigned()}
                        />
                      </div>
                      <Typography variant="body2" color="textSecondary">
                        {branch.address}
                      </Typography>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      title={t("branches.connection.reassign-edit")}
                      onClick={() => setBranchToReassign(branch)}
                      sx={{
                        margin: 0,
                        minWidth: "fit-content",
                        background: "transparent",
                        border: "1px solid #666",
                        borderRadius: "50%",
                        color: "#666",
                        textTransform: "none",
                        fontFamily: "Montserrat",
                        fontWeight: 300,
                        width: "2.5rem",
                        height: "2.5rem",
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g fill="#666">
                          <path d="M8.293 3.707 1 11v4h4l7.293-7.293-4-4ZM9.707 2.293l4 4 1.465-1.465a2.829 2.829 0 0 0-4-4L9.707 2.293Z" />
                        </g>
                      </svg>
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleConnect(branch)}
                      sx={{
                        backgroundColor: "#EF0BB8",
                        "&:hover": {
                          backgroundColor: "#d009a6",
                        },
                      }}
                    >
                      {t("branches.connection.authorize")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Box>
      </Modal>

      {branchToReassign && (
        <CloverReassignBranchModal
          isOpen={!!branchToReassign}
          onClose={() => setBranchToReassign(null)}
          branch={branchToReassign}
          onReassigned={onBranchReassigned}
        />
      )}
    </>
  );
}
